import React from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

/**
 * Shared panel for the 404 and 403 pages.
 *
 * Same design as the Reseller platform and the Drupal error pages
 * (isd_error_pages module): the navy to sky gradient with the highlight in
 * the top right, the status code as a watermark, and the requested path so a
 * typo is easy to spot. It renders inside the app's header and footer, which
 * carry the ISAFE branding.
 */

const Panel = styled.section`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 720px;
  margin: 40px auto;
  padding: clamp(24px, 5vw, 44px);
  border-radius: 24px;
  color: #fff;
  background:
    radial-gradient(circle at top right, rgba(127, 183, 229, 0.5), transparent 55%),
    linear-gradient(135deg, #0c1f36, #28639a);
  box-shadow: 0 10px 30px rgba(12, 30, 52, 0.08);
`;

// The status code repeated as a watermark, sized off the panel.
const Watermark = styled.span`
  position: absolute;
  right: -10px;
  bottom: -46px;
  font-size: clamp(9rem, 22vw, 15rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.04em;
  color: rgba(255, 255, 255, 0.07);
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  user-select: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;

  h1 {
    margin: 4px 0 0;
    font-size: clamp(1.5rem, 3.4vw, 2.05rem);
    line-height: 1.2;
    color: #fff;
  }

  p {
    margin: 0;
    max-width: 56ch;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.86);
  }
`;

const Eyebrow = styled.p`
  && {
    font-size: 0.8rem;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const CodeRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
`;

const Code = styled.strong`
  font-size: clamp(2.6rem, 6vw, 3.6rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
`;

const PathChip = styled.span`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-family: ui-monospace, Consolas, monospace;
  font-size: 0.78rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;

  button {
    padding: 10px 16px;
    border-radius: 12px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    background: #fff;
    color: #0c1e34;
    transition: transform 0.12s ease, background-color 0.12s ease;
  }

  button:hover {
    transform: translateY(-1px);
  }

  button.ghost {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.36);
  }

  button.ghost:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  button:focus-visible {
    outline: 2px solid #7fb7e5;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    button {
      transition: none;
    }
    button:hover {
      transform: none;
    }
  }
`;

const Details = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin: 16px 0 0;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);

  div {
    min-width: 0;
  }

  dt {
    font-size: 0.72rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.66);
  }

  dd {
    margin: 2px 0 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const ErrorPage = ({ code, eyebrow, title, message, actions = [], details = [], showPath = true }) => {
  const { pathname, search } = useLocation();
  const currentPath = `${pathname}${search}`;

  return (
    <Panel aria-labelledby="error-page-title">
      <Watermark aria-hidden="true">{code}</Watermark>
      <Content>
        <Eyebrow>{eyebrow}</Eyebrow>
        <CodeRow>
          <Code>{code}</Code>
          {showPath && currentPath ? <PathChip>{currentPath}</PathChip> : null}
        </CodeRow>
        <h1 id="error-page-title">{title}</h1>
        <p>{message}</p>

        {actions.length ? (
          <Actions>
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={action.variant === "ghost" ? "ghost" : undefined}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </Actions>
        ) : null}

        {details.length ? (
          <Details>
            {details.map((detail) => (
              <div key={detail.label}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </Details>
        ) : null}
      </Content>
    </Panel>
  );
};

export default ErrorPage;
